import api from '../../services/api';

const state = {
  licenses: [],
  currentLicense: null,
  loading: false,
  error: null
};

const getters = {
  allLicenses: state => state.licenses,
  licensesByProduct: state => productId => 
    state.licenses.filter(l => l.product_id === productId),
  currentLicense: state => state.currentLicense,
  isLoading: state => state.loading
};

const actions = {
  async fetchLicenses({ commit }, productId = null) {
    commit('SET_LOADING', true);
    try {
      const params = productId ? { product_id: productId } : {};
      const response = await api.get('/licenses', { params });
      commit('SET_LICENSES', response.data);
      return { success: true };
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.error || 'Failed to fetch licenses');
      return { success: false };
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  async fetchLicense({ commit }, id) {
    commit('SET_LOADING', true);
    try {
      const response = await api.get(`/licenses/${id}`);
      commit('SET_CURRENT_LICENSE', response.data);
      return { success: true, data: response.data };
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.error || 'Failed to fetch license');
      return { success: false };
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  async createLicense({ dispatch }, licenseData) {
    try {
      const response = await api.post('/licenses', licenseData);
      await dispatch('fetchLicenses', licenseData.product_id);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to create license' 
      };
    }
  },
  
  async createBulkLicenses({ dispatch }, data) {
    try {
      const response = await api.post('/licenses/bulk', data);
      await dispatch('fetchLicenses', data.product_id);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to create licenses' 
      };
    }
  },
  
  async updateLicense({ dispatch }, { id, data }) {
    try {
      const response = await api.put(`/licenses/${id}`, data);
      await dispatch('fetchLicenses');
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to update license' 
      };
    }
  },
  
  async deleteLicense({ dispatch }, id) {
    try {
      await api.delete(`/licenses/${id}`);
      await dispatch('fetchLicenses');
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to delete license' 
      };
    }
  },
  
  async addUserToLicense({ dispatch }, { licenseId, userEmail }) {
    try {
      await api.post(`/licenses/${licenseId}/users`, { user_email: userEmail });
      await dispatch('fetchLicense', licenseId);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to add user' 
      };
    }
  },
  
  async removeUserFromLicense({ dispatch }, { licenseId, userId }) {
    try {
      await api.delete(`/licenses/${licenseId}/users/${userId}`);
      await dispatch('fetchLicense', licenseId);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to remove user' 
      };
    }
  },
  
  async removeUser({ dispatch }, { licenseId, userEmail }) {
    try {
      await api.delete(`/licenses/${licenseId}/users`, { 
        data: { user_email: userEmail }
      });
      await dispatch('fetchLicenses');
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to remove user' 
      };
    }
  },
  
  async removeMachine({ dispatch }, { licenseId, machineId }) {
    try {
      await api.delete(`/licenses/${licenseId}/machines/${machineId}`);
      await dispatch('fetchLicenses');
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to remove machine' 
      };
    }
  },
  
  async removeMachineByMac({ dispatch }, { licenseId, macAddress }) {
    try {
      await api.delete(`/licenses/${licenseId}/machines`, {
        data: { mac_address: macAddress }
      });
      await dispatch('fetchLicenses');
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to remove machine'
      };
    }
  },

  // ─── Suite-bundle (Phase 4) ───────────────────────────────────────
  async createBundleLicense(_, data) {
    try {
      const res = await api.post('/licenses/bundle', data);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Failed to create bundle license' };
    }
  },

  async fetchEntitlements(_, licenseId) {
    try {
      const res = await api.get(`/licenses/${licenseId}/entitlements`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Failed to fetch entitlements' };
    }
  },

  async replaceEntitlements(_, { licenseId, product_entitlements }) {
    try {
      const res = await api.put(`/licenses/${licenseId}/entitlements`, { product_entitlements });
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Failed to update entitlements' };
    }
  },

  async updateSuiteConfig(_, { licenseId, data }) {
    try {
      const res = await api.put(`/licenses/${licenseId}/suite-config`, data);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Failed to update suite config' };
    }
  },

  // Recovery path for "I created the license but lost the key" — POST
  // (not GET) so it shows up clearly in audit + access logs as an
  // intentional reveal. Every call is audit-logged server-side.
  async revealLicenseKey(_, licenseId) {
    try {
      const res = await api.post(`/licenses/${licenseId}/reveal-key`);
      return { success: true, license_key: res.data.license_key };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Failed to reveal key' };
    }
  },
};

const mutations = {
  SET_LICENSES(state, licenses) {
    state.licenses = licenses;
  },
  SET_CURRENT_LICENSE(state, license) {
    state.currentLicense = license;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};